import { Box, Button, Flex, HStack, Select, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Loading } from "../../components/Loading";
import { useMovimentacaoFinanceira } from "../../hooks/contaCorrente/useMovimentacaoFinanceira";
import { MovimentoItem } from "./MovimentoItem";

function formatDate(value: string) {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
}

const meses = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Fev" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Abr" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Ago" },
  { value: 9, label: "Set" },
  { value: 10, label: "Out" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dez" },
];

export function Movimentos() {
  const now = new Date();
  const [periodo, setPeriodo] = useState<"mes" | "ultimos_7" | "ultimos_15" | "ultimos_30">("mes");
  const [mes, setMes] = useState<number>(now.getMonth() + 1);
  const [ano, setAno] = useState<number>(now.getFullYear());
  const [anoAplicado, setAnoAplicado] = useState<number>(now.getFullYear());
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(100);

  useEffect(() => {
    const savedPerPage = window.localStorage.getItem('movimentacao_per_page');
    if (savedPerPage) {
      const parsed = Number(savedPerPage);
      if (!Number.isNaN(parsed)) {
        const normalized = Math.min(500, Math.max(100, Math.ceil(parsed / 100) * 100));
        setPerPage(normalized);
      }
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem('movimentacao_per_page', String(perPage));
  }, [perPage]);

  const anos = useMemo(() => {
    const current = now.getFullYear();
    return Array.from({ length: 6 }, (_, i) => current - i);
  }, [now]);

  const { data, isLoading, isFetching } = useMovimentacaoFinanceira({
    mes,
    ano: anoAplicado,
    periodo,
    page,
    perPage,
  });

  if (isLoading) return <Loading />;
  if (typeof data === "undefined") return <Text>Não é possível carregar os movimentos</Text>;

  const movimentos = data.data || {};
  const meta = data.meta;
  const keys = Object.keys(movimentos);

  return (
    <Box
      gap={6}
      fontSize={"sm"}
      p={"5"}
      border={"1px solid #ccc"}
      display={"flex"}
      flexDir={"column"}
    >
      <Flex gap={4} alignItems={"center"} flexWrap={"wrap"}>
        <HStack spacing={3}>
          <Select
            size={"sm"}
            value={periodo}
            onChange={(e) => {
              const value = e.target.value as "mes" | "ultimos_7" | "ultimos_15" | "ultimos_30";
              setPeriodo(value);
              if (value === "mes") {
                setAnoAplicado(ano);
              }
              setPage(1);
            }}
            width={"170px"}
          >
            <option value="mes">Períodos</option>
            <option value="ultimos_7">Últimos 7 dias</option>
            <option value="ultimos_15">Últimos 15 dias</option>
            <option value="ultimos_30">Últimos 30 dias</option>
          </Select>
          <Select
            size={"sm"}
            value={mes}
            onChange={(e) => {
              setMes(Number(e.target.value));
              setAnoAplicado(ano);
              setPage(1);
            }}
            isDisabled={periodo === "ultimos_30"}
            width={"120px"}
          >
            {meses.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
          <Select
            size={"sm"}
            value={ano}
            onChange={(e) => {
              setAno(Number(e.target.value));
            }}
            isDisabled={periodo === "ultimos_30"}
            width={"120px"}
          >
            {anos.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
          <Select
            size={"sm"}
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            width={"140px"}
          >
            <option value={100}>100 / pág</option>
            <option value={200}>200 / pág</option>
            <option value={300}>300 / pág</option>
            <option value={400}>400 / pág</option>
            <option value={500}>500 / pág</option>
          </Select>
        </HStack>
        <HStack spacing={3}>
          <Text fontSize={"sm"} fontWeight={"semibold"} color={"gray.700"}>
            {meta?.periodo?.inicio && meta?.periodo?.fim
              ? `Período: ${formatDate(meta.periodo.inicio)} a ${formatDate(meta.periodo.fim)}`
              : ""}
          </Text>
          {isFetching && (
            <Text fontSize={"sm"} color={"gray.500"}>
              Atualizando...
            </Text>
          )}
        </HStack>
      </Flex>

      {keys.length === 0 ? (
        <Box>
          <Text textAlign={"center"} fontSize={"2xl"} fontWeight={"bold"}>
            Nenhum movimento encontrado
          </Text>
        </Box>
      ) : (
        <Box>
          <Table>
            <Thead>
              <Tr>
                <Th fontSize={"md"} fontWeight={"bold"} py={1} px={1} textTransform={"capitalize"}>
                  Transação
                </Th>
                <Th fontSize={"md"} fontWeight={"bold"} py={1} px={1} textTransform={"capitalize"}>
                  Info
                </Th>
                <Th fontSize={"md"} fontWeight={"bold"} py={1} px={1} textTransform={"capitalize"}>
                  Data Lançamento
                </Th>
                <Th fontSize={"md"} fontWeight={"bold"} py={1} px={1} textTransform={"capitalize"}>
                  Valor Bruto
                </Th>
                <Th fontSize={"md"} fontWeight={"bold"} py={1} px={1} textTransform={"capitalize"}>
                  Valor Conveniência
                </Th>
                <Th fontSize={"md"} fontWeight={"bold"} py={1} px={1} textTransform={"capitalize"}>
                  Taxa Parceiro
                </Th>
                <Th fontSize={"md"} fontWeight={"bold"} py={1} px={1} textTransform={"capitalize"}>
                  Valor Lançamento
                </Th>
                <Th fontSize={"md"} fontWeight={"bold"} py={1} px={1} textTransform={"capitalize"}>
                  Saldo
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {keys.map((key) => (
                <Fragment key={key}>
                  <Tr>
                    <Td colSpan={8} px={0} py={4}>
                      <Text textAlign={"start"} fontSize={"2xl"} fontWeight={"bold"}>
                        {key}
                      </Text>
                    </Td>
                  </Tr>
                  {movimentos[key].map((movimento) => (
                    <MovimentoItem key={movimento.id} item={movimento} />
                  ))}
                </Fragment>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {meta?.last_page && meta.last_page > 1 && (
        <Flex alignItems={"center"} justifyContent={"space-between"} gap={4}>
          <Text fontSize={"sm"} color={"gray.600"}>
            Página {meta.current_page} de {meta.last_page} — Total {meta.total}
          </Text>
          <HStack spacing={2}>
            <Button
              size={"sm"}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              isDisabled={meta.current_page <= 1}
            >
              Anterior
            </Button>
            <Button
              size={"sm"}
              onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              isDisabled={meta.current_page >= meta.last_page}
            >
              Próxima
            </Button>
          </HStack>
        </Flex>
      )}
    </Box>
  );
}
